import { useState, type FormEvent } from "react";

const FORM_ENDPOINT = "https://readdy.ai/api/form/daio1prp14a1h3mr77ug";

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "contact_alt") {
        return;
      }
      const text = String(value);
      if (text.trim() === "") {
        return;
      }
      payload.append(key, text);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = String(serverMsg ?? "").toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK";

      if (isOk && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          isSpam
            ? "提交内容被识别为异常，请检查后重新填写。"
            : serverMsg || "提交失败，请稍后再试。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络开小差了，请稍后重试，或直接发邮件给我们。");
    }
  };

  return (
    <form
      data-readdy-form
      id="cg-contact-form"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-7"
    >
      <h3 className="font-heading text-lg font-bold text-foreground-950">给我们留言反馈</h3>
      <p className="mt-2 text-sm leading-relaxed text-foreground-600">
        发现内容有误、想补充线索，或者单纯想说点什么，都可以在这里告诉我们。
      </p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="nickname" className="text-xs font-label font-medium text-foreground-600">
            你的称呼
          </label>
          <input
            id="nickname"
            name="nickname"
            type="text"
            required
            maxLength={40}
            placeholder="怎么称呼你"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm font-label text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="email" className="text-xs font-label font-medium text-foreground-600">
            联系邮箱
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            placeholder="方便我们回复你"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm font-label text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-1.5">
        <label htmlFor="feedbackType" className="text-xs font-label font-medium text-foreground-600">
          反馈类型
        </label>
        <select
          id="feedbackType"
          name="feedbackType"
          defaultValue="内容纠错"
          className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm font-label text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
        >
          <option value="内容纠错">内容纠错</option>
          <option value="线索补充">线索补充</option>
          <option value="功能建议">功能建议</option>
          <option value="合作与媒体">合作与媒体</option>
          <option value="其他">其他</option>
        </select>
      </div>

      <div className="mt-4 flex flex-col gap-1.5">
        <label htmlFor="message" className="text-xs font-label font-medium text-foreground-600">
          具体内容
        </label>
        <textarea
          id="message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="最多输入 500 字"
          className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm font-label text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
        ></textarea>
      </div>

      <div className="cg-form-alt-field" aria-hidden="true">
        <label htmlFor="contact_alt">请勿填写此项</label>
        <input
          id="contact_alt"
          name="contact_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      {status === "error" && (
        <p className="mt-4 flex items-start gap-2 rounded-md bg-secondary-50 px-3 py-2.5 text-sm font-label text-secondary-800">
          <i className="ri-error-warning-line mt-0.5"></i>
          {formError}
        </p>
      )}
      {status === "success" && (
        <p className="mt-4 flex items-start gap-2 rounded-md bg-accent-50 px-3 py-2.5 text-sm font-label text-accent-900">
          <i className="ri-checkbox-circle-line mt-0.5"></i>
          收到啦，感谢你的反馈，我们会在工作日 24 小时内处理。
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-5 flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
        {status === "submitting" ? "提交中…" : "提交反馈"}
      </button>
    </form>
  );
}